import { useState, useCallback } from 'react';
import { Box, FormHelperText, FormLabel, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Editor from '@monaco-editor/react';
import type { OnValidate } from '@monaco-editor/react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type Props = FieldExtensionComponentProps<string, { rows?: number }>;

const useStyles = makeStyles(theme => ({
  errorAlert: {
    marginBottom: theme.spacing(1),
    maxHeight: '130px',
    overflowY: 'auto',
  },
  errorList: {
    margin: 0,
    paddingLeft: theme.spacing(2),
  },
}));

export const JsonInputValidator = (props: Props) => {
  const { onChange, schema, uiSchema } = props;
  const classes = useStyles();
  const title = schema.title || uiSchema?.['ui:title'] || 'JSON Input';
  const description = schema.description || uiSchema?.['ui:description'] || '';
  const required = schema.required && schema.required.length > 0;

  // Get the current value - handle both string and parsed JSON
  let currentValue = '';
  if (typeof props.formData === 'string') {
    currentValue = props.formData;
  } else if (props.formData) {
    currentValue = JSON.stringify(props.formData, null, 2);
  }

  const [value, setValue] = useState(currentValue);
  const [error, setError] = useState<string[] | null>(null);

  const handleEditorChange = useCallback(
    (newValue: string | undefined) => {
      if (!newValue) return;

      setValue(newValue);
      onChange(newValue);
    },
    [onChange],
  );

  const handleValidate: OnValidate = useCallback(markers => {
    setError(
      markers.length > 0
        ? markers.map(
            marker =>
              `Code ${marker.code} | ${marker.message} [Ln ${marker.startLineNumber}, Col ${marker.startColumn}]`,
          )
        : null,
    );
  }, []);

  return (
    <Box>
      <FormLabel required={required}>{title}</FormLabel>
      <FormHelperText>{description}</FormHelperText>
      {error && (
        <Alert severity="error" className={classes.errorAlert}>
          Fix the following errors:
          <ul className={classes.errorList}>
            {error.map(newError => (
              <li key={newError}>{newError}</li>
            ))}
          </ul>
        </Alert>
      )}
      <Editor
        height="500px"
        defaultLanguage="json"
        value={value}
        onChange={handleEditorChange}
        onValidate={handleValidate}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          formatOnPaste: true,
          formatOnType: false, // Disable auto-format on type to avoid conflicts
          tabSize: 2,
        }}
      />
    </Box>
  );
};

// Field extension registration moved to plugin.ts following Backstage best practices
